/**
 * Тултип (§2.3 «Тултип <Tooltip>», §13).
 *
 * Появление через 400 ms, исчезновение сразу, Esc скрывает. Показывается и по
 * фокусу с клавиатуры — иначе подсказка недоступна без мыши (П5).
 *
 * Триггер оборачивается в inline-flex обёртку: так тултип получает элемент для
 * `getBoundingClientRect`, не трогая ref самого триггера. Доступное имя не
 * подменяется — текст уходит в `aria-describedby`, то есть читается в
 * дополнение к подписи, а не вместо неё.
 */
import { cloneElement, useEffect, useId, useRef, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { Popover } from './Popover'
import type { PopoverPlacement } from './Popover'
import './Tooltip.css'

export interface TooltipProps {
  content: ReactNode
  /** Триггер: любой элемент, который примет `aria-describedby`. */
  children: ReactElement<{ 'aria-describedby'?: string }>
  /** Задержка появления, мс. По умолчанию 400 (§2.3). */
  delay?: number
  placement?: PopoverPlacement
  /** Отключить подсказку, не меняя разметку (например, значение не обрезано). */
  disabled?: boolean
}

export function Tooltip({
  content,
  children,
  delay = 400,
  placement = 'bottom-start',
  disabled = false,
}: TooltipProps) {
  const id = `tooltip${useId()}`
  /* Узел якоря держим в состоянии и ставим callback-ref'ом. Через useRef он
     не доезжал до поповера: ref заполняется после рендера, перерисовки не
     происходит, и поповер навсегда оставался с anchor = null — то есть
     подсказки не показывались вообще нигде. */
  const [anchorNode, setAnchorNode] = useState<HTMLElement | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [open, setOpen] = useState(false)

  function clearTimer() {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  function show() {
    if (disabled) return
    clearTimer()
    timerRef.current = setTimeout(() => setOpen(true), delay)
  }

  /** Исчезновение — сразу, без задержки (§2.3). */
  function hide() {
    clearTimer()
    setOpen(false)
  }

  useEffect(() => clearTimer, [])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open])

  const trigger = cloneElement(children, {
    'aria-describedby': open ? id : children.props['aria-describedby'],
  })

  return (
    <>
      <span
        ref={setAnchorNode}
        className="tooltip-anchor"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={(event) => {
          /* Только клавиатурный фокус: по клику мышью подсказка не нужна (§2.3). */
          const target = event.target
          if (target instanceof Element && target.matches(':focus-visible')) show()
        }}
        onBlur={hide}
      >
        {trigger}
      </span>

      <Popover
        open={open && !disabled}
        anchor={anchorNode}
        onClose={hide}
        role="none"
        placement={placement}
        bare
        returnFocus={false}
        closeOnOutsideClick={false}
      >
        <div id={id} role="tooltip" className="tooltip t-meta">
          {content}
        </div>
      </Popover>
    </>
  )
}
